import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../../lib/utils';
import { Loader2, RefreshCw, Briefcase, Users, Building2, GraduationCap, HeartHandshake, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';

const fmt = (n) => (typeof n === 'number' ? n.toLocaleString() : '—');

const Stat = ({ label, value, hint, testid }) => (
  <div className="bg-white rounded-xl p-4 border border-gray-100" data-testid={testid}>
    <p className="text-2xl font-black text-primary tabular-nums">{fmt(value)}</p>
    <p className="text-sm font-semibold text-gray-700 mt-0.5">{label}</p>
    {hint && <p className="text-xs text-gray-400 mt-0.5">{hint}</p>}
  </div>
);

const Section = ({ icon: Icon, title, children }) => (
  <div className="mb-6">
    <h3 className="flex items-center gap-2 font-serif text-lg font-bold text-primary mb-3"><Icon className="w-5 h-5 text-secondary" /> {title}</h3>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{children}</div>
  </div>
);

const AdminStatistics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/admin/statistics`);
      setData(r.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="py-16 text-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  if (!data) return <p className="text-center text-gray-500 py-10">Could not load statistics.</p>;

  const gen = data.generated_at ? new Date(data.generated_at).toLocaleString() : '';

  return (
    <div data-testid="admin-statistics">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-serif text-2xl font-bold text-primary">INASW Statistics</h2>
          <p className="text-sm text-gray-500">The official live numbers used across the site (single source of truth). Last updated {gen}.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="stats-refresh"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
      </div>

      <Section icon={Briefcase} title="Jobs">
        <Stat label="Active Jobs" value={data.jobs.active_jobs} hint="Live, published, non-test" testid="stat-active-jobs" />
        <Stat label="Jobs Posted Since Launch" value={data.jobs.jobs_posted_since_launch} hint="All-time, non-test" />
        <Stat label="Employer Direct (active)" value={data.jobs.employer_direct_active} />
        <Stat label="External (active)" value={data.jobs.external_active} />
        <Stat label="Expired" value={data.jobs.expired_total} />
      </Section>

      <Section icon={Users} title="Members & Accounts">
        <Stat label="Social Worker Accounts" value={data.members.social_worker_accounts} />
        <Stat label="Employer Accounts" value={data.members.employer_accounts} />
        <Stat label="LCSW Supervisors" value={data.members.lcsw_supervisors} />
        <Stat label="Total Registered Users" value={data.members.total_registered_users} />
      </Section>

      <Section icon={Building2} title="Private Practice Directory">
        <Stat label="Active Practices" value={data.private_practice.total_active} testid="stat-pp-active" />
        <Stat label="FREE Listings" value={data.private_practice.free} />
        <Stat label="Featured ($14.99)" value={data.private_practice.featured} />
        <Stat label="Practice Pro ($29.99)" value={data.private_practice.pro} />
        <Stat label="Group Practice ($49.99)" value={data.private_practice.group} />
      </Section>

      <Section icon={HeartHandshake} title="Directory & Resources">
        <Stat label="Community Resources" value={data.directory.community_resources} />
        <Stat label="Internships" value={data.directory.internships} />
        <Stat label="Social Work Forms" value={data.directory.social_work_forms} />
        <Stat label="BH Assessments" value={data.directory.behavioral_health_instruments} />
      </Section>

      {(data.data_quality.suspected_test_users > 0 || data.data_quality.suspected_test_jobs > 0) && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3" data-testid="stats-data-quality">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-800 text-sm">Suspected test/demo records detected</p>
            <p className="text-sm text-amber-700">{fmt(data.data_quality.suspected_test_users)} user(s) and {fmt(data.data_quality.suspected_test_jobs)} job(s) match test/QA/demo patterns. {data.data_quality.note}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminStatistics;
